import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Stack,
} from '@mui/material'
import { ButtonSecondary } from 'components/ButtonSecondary'
import FormInput from 'components/FormInput'
import InputMask from 'components/FormInputMask'
import { useAddress } from 'hooks/address.hook'
import React, { ChangeEvent, useEffect, useState } from 'react'
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFieldArrayRemove,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { NaturalPersonAddressType } from 'types/address.type'
import { formatZipCode } from 'utils/text.util'
import { FormInputAutoComplete } from 'components/FormInputAutoComplete'
import { ItemOption } from 'components/InputSelect'
import FormInputCep from 'components/FormInputCEP'

interface AddressItemProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  watch: UseFormWatch<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  name: Path<TFieldValues>

  index: number
  listValues?: NaturalPersonAddressType[] | null
  onRemove: UseFieldArrayRemove
  disabled?: boolean
}

export const AddressItem = <TFieldValues extends FieldValues>({
  control,
  watch,
  setValue,
  name,

  index,
  listValues,
  onRemove,
  disabled,
}: AddressItemProps<TFieldValues>) => {
  const cepField = `${name}.${index}.endereco.cep` as Path<TFieldValues>
  const logradouroField = `${name}.${index}.endereco.logradouro` as Path<TFieldValues>
  const numeroField = `${name}.${index}.endereco.numero` as Path<TFieldValues>
  const bairroField = `${name}.${index}.endereco.bairro.id` as Path<TFieldValues>
  const complementoField = `${name}.${index}.endereco.complemento` as Path<TFieldValues>
  const ufField = `${name}.${index}.endereco.cidade.uf.id` as Path<TFieldValues>
  const cidadeField = `${name}.${index}.endereco.cidade.id` as Path<TFieldValues>
  const enderecoPrincipalField = `${name}.${index}.enderecoPrincipal` as Path<TFieldValues>

  const zipCodeValue = watch(cepField)
  const stateIdValue = watch(ufField)
  const cityIdValue = watch(cidadeField)
  const mainAddressValue = watch(enderecoPrincipalField)

  const [disabledEnderecoPrincipal, setDisabledEnderecoPrincipal] = useState<boolean>(false)

  const {
    address: { stateOptions, cityOptions, neighborhoodOptions },
    getAddressByZipCode,
  } = useAddress(String(stateIdValue), String(cityIdValue))

  const handleFillAddressFields = async (): Promise<void> => {
    if (zipCodeValue) {
      const formattedZipCode = formatZipCode(zipCodeValue)
      const res = await getAddressByZipCode(formattedZipCode)
      if (!res) return
      const neighborhoodValue: string = res.bairroId ? res.bairroId : '0'
      const cityValue: string = res.cidadeId ? res.cidadeId : '0'
      setValue(cepField, res.cep as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(ufField, Number(res.ufId) as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(
        bairroField,
        Number(neighborhoodValue) as PathValue<TFieldValues, Path<TFieldValues>>,
      )
      setValue(cidadeField, Number(cityValue) as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(logradouroField, res.logradouro as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(complementoField, res.complemento as PathValue<TFieldValues, Path<TFieldValues>>)
    }
  }

  const handleDisableSearchZipCode = (): boolean => {
    if (disabled) return true
    let disableSearchZipCode = true
    if (zipCodeValue && zipCodeValue.length >= 7) {
      disableSearchZipCode = false
    }
    return disableSearchZipCode
  }

  useEffect(() => {
    if (listValues && listValues.length === 1) {
      listValues.forEach((_, i) =>
        setValue(
          `${name}.${i}.enderecoPrincipal` as Path<TFieldValues>,
          true as PathValue<TFieldValues, Path<TFieldValues>>,
        ),
      )
      setDisabledEnderecoPrincipal(true)
    } else {
      setDisabledEnderecoPrincipal(false)
    }
  }, [listValues])

  const handleChangeMainAddress = (): void => {
    if (mainAddressValue && listValues && listValues.length > 0) {
      listValues.forEach((item, i) => {
        if (i !== index && item.enderecoPrincipal) {
          setValue(
            `${name}.${i}.enderecoPrincipal` as Path<TFieldValues>,
            false as PathValue<TFieldValues, Path<TFieldValues>>,
          )
        }
      })
    }
  }

  useEffect(() => {
    if (stateIdValue && String(stateIdValue) === '0') {
      setValue(cidadeField, null as PathValue<TFieldValues, Path<TFieldValues>>)
      setValue(bairroField, null as PathValue<TFieldValues, Path<TFieldValues>>)
    } else if (cityIdValue && String(cityIdValue) === '0') {
      setValue(bairroField, null as PathValue<TFieldValues, Path<TFieldValues>>)
    }
  }, [index, setValue, stateIdValue, cityIdValue])

  useEffect(() => {
    if (mainAddressValue) {
      handleChangeMainAddress()
    }
  }, [mainAddressValue])

  const handleDisabledEnderecoPrincipal = () => {
    if (disabled) return true
    return disabledEnderecoPrincipal
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>, onChange: (...event: any[]) => void) => {
    let choiceChecked: boolean = e.target.checked

    const updatedCheckboxes = listValues ? [...listValues] : []
    updatedCheckboxes[index].enderecoPrincipal = choiceChecked

    const selectedCount = listValues?.filter((item) => item.enderecoPrincipal).length

    choiceChecked = selectedCount === 0 ? true : choiceChecked

    onChange(choiceChecked)
    setValue(enderecoPrincipalField, choiceChecked as PathValue<TFieldValues, Path<TFieldValues>>)
  }

  return (
    <Paper variant='outlined' sx={{ p: 3, width: '100%', backgroundColor: 'white' }}>
      <Grid container sx={{ mb: 2 }}>
        <Grid item md={12}>
          <Stack direction='row'>
            <FormGroup sx={{ flex: 1 }}>
              <Controller
                name={enderecoPrincipalField}
                control={control}
                render={({ field: { value: value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        color='secondary'
                        checked={value}
                        onChange={(e) => handleChange(e, onChange)}
                        disabled={handleDisabledEnderecoPrincipal()}
                      />
                    }
                    value={value || false}
                    label='Endereço principal'
                  />
                )}
              />
            </FormGroup>
            {index >= 1 && (
              <IconButton onClick={() => onRemove(index)} disabled={disabled}>
                <DeleteOutlineRoundedIcon color={!disabled ? 'error' : 'action'} />
              </IconButton>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={2} rowSpacing={4}>
        <Grid item md={6} xs={12}>
          <Stack direction='row' spacing={1}>
            <FormInputCep
              control={control}
              name={cepField}
              label='CEP'
              placeholder='Informe o CEP'
              sx={{ flex: 1 }}
              disabled={disabled}
            />
            <ButtonSecondary
              maxHeight={40}
              startIcon={<SearchRoundedIcon />}
              label='Consultar'
              disabled={handleDisableSearchZipCode()}
              onClick={handleFillAddressFields}
            />
          </Stack>
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputAutoComplete
            control={control}
            name={ufField}
            label='Estado'
            options={stateOptions}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => Number(option.value) === Number(id)) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => Number(option?.value) || null}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputAutoComplete
            control={control}
            name={cidadeField}
            label='Cidade'
            options={cityOptions}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => Number(option.value) === Number(id)) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => Number(option?.value) || null}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInputAutoComplete
            control={control}
            name={bairroField}
            label='Bairro'
            options={neighborhoodOptions}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            getOptionById={(id: string | null, options: ItemOption[]) =>
              options.find((option) => Number(option.value) === Number(id)) || null
            }
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            onChangeValue={(option) => Number(option?.value) || null}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <FormInput
            control={control}
            name={logradouroField}
            label='Logradouro'
            placeholder='Informe o logradouro'
            maxLength={60}
            disabled={disabled}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <InputMask
            control={control}
            name={numeroField}
            label='N°'
            mask='0000000000'
            disabled={disabled}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <FormInput
            control={control}
            name={complementoField}
            label='Complemento'
            placeholder='Informe o complemento'
            maxLength={20}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}
